import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PhoneInput from '@/components/PhoneInput';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

const inputCls = 'rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 text-[16px]';

export default function RegisterPage() {
  const { register, verifySignupOTP, resendOTP } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [otp, setOtp] = useState('');
  const [channel, setChannel] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await register(phone, name, password);
      setChannel(res?.data?.channel || 'sms');
      setStep('otp');
    } catch (err) {
      setError(err?.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await verifySignupOTP(phone, otp);
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.error || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setError('');
    try {
      const res = await resendOTP(phone, 'signup');
      setChannel(res?.data?.channel || 'sms');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to resend OTP');
    }
  };

  const label = (t) => (<span className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block">{t}</span>);

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-16 bg-background" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="eyebrow mb-4">Begin</div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 48 }}>
            {step === 'form' ? 'Create your account' : 'Verify your number'}
          </h1>
          <p className="mt-4 text-ink/70" style={{ fontSize: 15 }}>
            {step === 'form' ? 'Premium custom tailoring, made for you' : `OTP sent via ${(channel || 'sms').toUpperCase()} to ${phone}`}
          </p>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
            <div>
              <Label htmlFor="name">{label('Full Name')}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your full name" data-testid="name-input" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="phone">{label('Mobile Number')}</Label>
              <PhoneInput id="phone" value={phone} onChange={setPhone} required data-testid="phone-input" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="password">{label('Password')}</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" className={`${inputCls} pr-10`} data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-0 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm">{label('Confirm Password')}</Label>
              <Input id="confirm" type={showPassword ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="confirm-password-input" className={inputCls} />
            </div>

            {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="register-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="register-btn" className="w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed" style={{ fontSize: 13 }}>
              {loading ? 'Sending OTP…' : (<>Create Account <ArrowRight className="w-4 h-4" /></>)}
            </button>

            <p className="text-center text-ink-muted pt-4" style={{ fontSize: 14 }}>
              Already have an account?{' '}
              <Link to="/login" className="text-ink font-medium underline underline-offset-4 hover:text-gold">Sign in</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-6" data-testid="otp-form">
            <div>
              <Label htmlFor="otp">{label('One-time Password')}</Label>
              <Input id="otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} placeholder="6-digit code" data-testid="otp-input" className={`${inputCls} text-center text-[42px] leading-none tracking-[0.4em] font-display font-semibold py-2`} />
            </div>
            {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="otp-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="verify-otp-btn" className="w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-50" style={{ fontSize: 13 }}>
              {loading ? 'Verifying…' : (<>Verify & Continue <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={() => setStep('form')} className="text-ink-muted hover:text-ink uppercase tracking-luxe text-[12px] font-medium" data-testid="back-btn">← Back</button>
              <button type="button" onClick={handleResend} className="text-ink hover:text-gold underline underline-offset-4 uppercase tracking-luxe text-[12px] font-medium" data-testid="resend-otp-btn">Resend OTP</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
